import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppContext } from "../../context/AppContext";
import { useBrand } from "../../context/BrandContext";
import { useCopy } from "../../context/CopyContext";
import { useIrisControl } from "../../hooks/useIrisControl";
import Header from "../Header";
import Heading from "../Heading";
import Button from "../Button";
import PoweredBy from "../PoweredBy";
import ItemsModalView from "./ItemsModalView";

const RemoveEmptyView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, dispatch } = useAppContext();
  const { brandConfig, isDarkMode } = useBrand();
  const { getCopy } = useCopy();
  const { openIris, closeIris, irisState, isConnected: wsConnected } = useIrisControl();
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [irisOpenAttempted, setIrisOpenAttempted] = useState(false);
  
  // Check if this is a back navigation from email capture
  const isBackFromEmail = location.state?.fromEmailCapture === true;
  
  // Get the most recently added product
  const lastAddedProduct = state.lastAddedProduct;
  
  // Use global dispensed state to determine if item is removed
  const isRemoved = state.kiosk.isItemDispensed || isBackFromEmail;

  const handleBack = () => {
    // Close iris when canceling (only if it's open)
    if (irisState === 'open') {
      closeIris();
    }
    navigate("/empty-accepted");
  };

  const handleAddAnother = () => {
    // Iris has already auto-closed after timer - no need to close again
    dispatch({ type: 'SET_ITEM_DISPENSED', payload: false });
    navigate("/add-empty");
  };

  const handleContinue = () => {
    // Close iris only if it's still open
    if (irisState === 'open') {
      closeIris();
    }
    // Navigate to email capture - items remain in cart for checkout
    dispatch({ type: "SET_STEP", payload: 6 });
    navigate("/email-capture");
  };

  const handleDone = () => {
    console.log('✅ [RemoveEmptyView] Done button clicked:', {
      irisState,
      willClose: irisState === 'open',
      timestamp: new Date().toISOString()
    });
    
    // Close iris only if it's still open (avoid redundant close after auto-close)
    if (irisState === 'open') {
      console.log('✅ [RemoveEmptyView] Closing iris because still open');
      closeIris();
    } else {
      console.log('✅ [RemoveEmptyView] Skipping close - iris already closed');
    }
    
    // Add the empty to cart if we have a product
    if (lastAddedProduct) {
      dispatch({ type: 'ADD_EMPTY', payload: lastAddedProduct });
    }
    
    // Mark item as physically removed from tray
    dispatch({ type: 'SET_ITEM_DISPENSED', payload: true });
  };

  // Open iris when entering view (unless coming back from email)
  useEffect(() => {
    console.log('🎬 [RemoveEmptyView] useEffect triggered:', {
      isBackFromEmail,
      wsConnected,
      irisOpenAttempted,
      willOpenIris: !isBackFromEmail && wsConnected && !irisOpenAttempted,
      timestamp: new Date().toISOString()
    });
    
    // Only open iris if:
    // 1. Not coming back from email
    // 2. WebSocket is connected
    // 3. Haven't already attempted to open
    if (!isBackFromEmail && wsConnected && !irisOpenAttempted) {
      dispatch({ type: 'SET_ITEM_DISPENSED', payload: false });
      setIrisOpenAttempted(true);
      
      console.log('🎬 [RemoveEmptyView] Opening iris for 3 seconds (first attempt only)');
      openIris(3);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isBackFromEmail, wsConnected]);

  // NOTE: We do NOT remove items from the cart when Done is clicked
  // The Done button only confirms physical removal from the tray
  // Items remain in the cart for checkout
  // useEffect(() => {
  //   if (isRemoved && lastAddedProduct && !isBackFromEmail && state.kiosk.isItemDispensed) {
  //     dispatch({ type: 'REMOVE_EMPTY', payload: lastAddedProduct.id });
  //   }
  // }, [isRemoved, lastAddedProduct, dispatch, isBackFromEmail, state.kiosk.isItemDispensed]);

  // NOTE: We no longer auto-navigate since items stay in cart
  // User must explicitly choose "Add Another" or "Continue"

  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };

  return (
    <div
      className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden"
      data-node-id="place-empty-view"
    >
      {/* Breathing Animation Background - Only show when not removed */}
      {!isRemoved && (
        <motion.div
          className="absolute bottom-[-675px] left-0 w-[1080px] h-[1080px] rounded-[1000px] pointer-events-none"
          style={{
            background: brandConfig.colors.primary[isDarkMode ? 'dark' : 'light'],
            filter: 'blur(200px)',
          }}
          animate={{
            y: [0, -100, 0],          
            opacity: [.25, .75, .25],
            scale: [1, 1.25, 1],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      )}

      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col gap-24">
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={state.itemsCount > 0}
              itemsNumber={state.itemsCount.toString()}
              onItemsClick={handleItemsClick}
            />
          </div>
        </div>

        {/* Main Content Area - with 3-row grid */}
        <motion.div
          className="flex-1 grid grid-rows-3 gap-0 mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {!isRemoved ? (
            <>
              {/* Row 1 - Main heading */}
              <div className="flex items-center justify-center">
                <div className="text-center w-full max-w-[920px]">
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 0.6,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                  >
                    <Heading 
                      title="Remove empty" 
                      subtitle="Please take your empty from the tray"
                    />
                  </motion.div>
                </div>
              </div>

              {/* Rows 2 & 3 - Product image and details spanning two rows */}
              <div className="row-span-2 flex flex-col items-center justify-center">
                {/* Product image - taking all available space minus text height */}
                <motion.div
                  className="w-full flex-1 bg-center bg-cover bg-no-repeat"
                  style={{
                    backgroundImage: lastAddedProduct?.image
                      ? `url('${lastAddedProduct.image}')`
                      : undefined,
                    backgroundSize: "contain",
                    backgroundRepeat: "no-repeat",
                  }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{
                    duration: 0.8,
                    delay: 0.2,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                >
                  {!lastAddedProduct?.image && (
                    <div className="w-full h-full bg-gray-100 rounded-lg flex items-center justify-center">
                      <span className="text-gray-400 text-2xl">No image</span>
                    </div>
                  )}
                </motion.div>
                
                {/* Product details - natural flow at bottom */}
                <motion.div
                  className="text-center w-full max-w-[920px] flex-shrink-0"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: 0.4,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                >
                  <div className="flex flex-col gap-4 items-center py-4">
                    <h3 className="font-brand-secondary font-normal text-[32px] leading-[42px] text-brand-text-primary text-center">
                      {lastAddedProduct ? lastAddedProduct.name : getCopy('productFallback.defaultProductName')}
                    </h3>
                    <div className="flex items-center justify-center">
                      <span className="font-brand-secondary font-normal text-[24px] leading-[31px] text-brand-text-muted text-center">
                        {lastAddedProduct ? lastAddedProduct.size : getCopy('productFallback.defaultProductSize')}
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </>
          ) : (
            /* Removed state - heading takes all 3 rows */
            <div className="row-span-3 flex items-center justify-center">
              <div className="text-center w-full max-w-[920px]">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ 
                    duration: 0.4, 
                    ease: [0.4, 0.0, 0.2, 1],
                    opacity: { duration: 0.3 },
                    y: { duration: 0.4 }
                  }}
                >
                  <Heading 
                    title="Empty removed" 
                    subtitle="Add another empty or continue"
                  />
                </motion.div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Bottom Section - with padding */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10">
          {/* Conditional Buttons */}
          <AnimatePresence mode="wait">
            {!isRemoved ? (
              <motion.div 
                key="action-buttons"
                className="flex flex-row gap-6 lg:gap-10 w-full"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ 
                  duration: 0.4, 
                  ease: [0.4, 0.0, 0.2, 1],
                  opacity: { duration: 0.3 },
                  y: { duration: 0.4 }
                }}
              >
                <Button variant="secondary" onClick={handleBack}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleDone}>
                  Done
                </Button>
              </motion.div>
            ) : (
              <motion.div 
                key="removed-buttons"
                className="flex flex-row gap-6 lg:gap-10 w-full"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ 
                  duration: 0.4, 
                  ease: [0.4, 0.0, 0.2, 1],
                  opacity: { duration: 0.3 },
                  y: { duration: 0.4 }
                }}
              >
                <Button variant="secondary" onClick={handleAddAnother}>
                  Add Another
                </Button>
                <Button variant="primary" onClick={handleContinue}>
                  Continue
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>

      {/* Items Modal */}
      <ItemsModalView
        isOpen={showItemsModal}
        onClose={handleCloseItemsModal}
      />
    </div>
  );
};

export default RemoveEmptyView;
